<template>
    <div class="container">
        <div class="title-container">
            <span>
                {{ $t('reglement.fournisseur.detail') }} {{ this.numeroReglement }}
                <span class="regles-pastilles-group">
                    <ReglePastille cle="reglementsFournisseur.definition" />
                    <ReglePastille cle="reglementFournisseurDetail.facturesLiees" />
                </span>
            </span>
        </div>
        <BorderedIconButton title="RETOUR" :imgUrl="imgUrl" className="back-btn" :onClick="this.$router.back" />
        <DataList v-if="this.reglements && this.isLoaded"
                  :data="this.reglements" :stop-propa-on-pdf="true"
                  @select="goToPage"/>
        <WaitingModal v-if="isAttachmentLoading"/>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import DataList from '@/components/ui-components/DataList.vue';
import { statusName } from '@/components/ui-components/StatusTag.vue';
import BorderedIconButton from '@/components/ui-components/BorderedIconButton.vue';
import ErrorManager from '@/common/utils/ErrorManager';
import { ERROR } from '@/modules/ErrorToaster/store/mutation-types';
import { store } from '@/plugins/store';
import WaitingModal from '@/components/ui-components/WaitingModal.vue';
import router from '@/router';
import ReglementService from '@/modules/ReglementsFournisseurs/services/ReglementService';
import dayjs from 'dayjs';
import { FactureFournisseurDTO } from '@/models/FactureFournisseurDTO';
import { MENU } from '@/components/layout/store/mutation-types';
import { MENU_FOURNISSEUR, MENU_FOURNISSEUR_SWITCH_CLIENT } from '@/components/layout/store/state';
import ReglePastille from '@/components/regles/ReglePastille.vue';

type DataType = {
    reglements: Array<any>;
    status: string | null;
    isLoaded: boolean;
    numeroReglement: string
};

export default defineComponent({
    name: 'FactureFournisseurPage',
    components: { DataList, BorderedIconButton, WaitingModal, ReglePastille },
    data(): DataType {
        return {
            reglements: [],
            isLoaded: false,
            status: null,
            numeroReglement: ''
        };
    },
    beforeMount() {
        if (localStorage.getItem('isFournisseur') === 'true' && localStorage.getItem('isClient') === 'true') {
            store.commit(`menuState/${MENU.SET_MENU}`, MENU_FOURNISSEUR_SWITCH_CLIENT);
        } else {
            store.commit(`menuState/${MENU.SET_MENU}`, MENU_FOURNISSEUR);
        }
        ReglementService.getReglementById(+this.$route.params.id)
            .then((res: any) => {
                this.status = statusName(res.data.statut);
                this.reglements = [
                    { label: this.$t('reglement.fournisseur.societe'), value: res.data.societe },
                    { label: this.$t('reglement.fournisseur.date'), value: dayjs(res.data.dateReglement).format('DD/MM/YYYY') },
                    { label: this.$t('reglement.fournisseur.nom.fournisseur'), value: res.data.fournisseur },
                    { label: this.$t('reglement.fournisseur.site'), value: res.data.site },
                    { label: this.$t('reglement.fournisseur.montant'), value: res.data.montantTTC.toLocaleString('fr-FR') + ' ' + this.$t('devise.'+res.data.devise) },
                ];

                this.numeroReglement = res.data.numero;

                const tableHeader = [
                    { label: 'id' },
                    { label: this.$t('reglement.fournisseur.table.factures.societe') },
                    { label: this.$t('reglement.fournisseur.table.factures.date') },
                    { label: this.$t('reglement.fournisseur.table.factures.echeance') },
                    { label: this.$t('reglement.fournisseur.table.factures.numero') },
                    { label: this.$t('reglement.fournisseur.table.factures.montant') },
                    { label: this.$t('reglement.fournisseur.table.factures.solde') },
                    { label: this.$t('reglement.fournisseur.table.factures.type') },
                ];

                const tableData = res.data.factures.map(
                    ({ id, societe, date, dateEcheance, numero, montantTTC, montantRestantTTC, devise, typeFacture }: FactureFournisseurDTO) => ({
                        id: { pageName: 'factureFournisseurPage', value: id, label: id },
                        societe: societe,
                        date: dayjs(date).format('DD/MM/YYYY'),
                        echeance: dayjs(dateEcheance).format('DD/MM/YYYY'),
                        numero: numero,
                        montantTTC: montantTTC + ' ' + this.$t('devise.' + devise),
                        solde: montantRestantTTC + ' ' + this.$t('devise.' + devise),
                        type: typeFacture
                    })
                );

                const noDataText = this.$t('interventionRequest.singlePage.noIntervention');

                this.reglements.push({
                    label: this.$t('reglement.fournisseur.liste.factures'),
                    value: { tableHeader, tableData, noDataText}
                });

                this.isLoaded = true;

            })
            .catch(async (err: any) => {
                const message = await ErrorManager.processError(err.response);

                this.$store.commit(`errorToaster/${ERROR.SET_ACTIVE}`, true);
                this.$store.commit(`errorToaster/${ERROR.SET_MESSAGE}`, message);
            });
    },
    computed: {
        imgUrl() {
            return require('@/assets/img/chevron-left-white.svg');
        },
        attachment() {
            return store.getters['suiviDemandeInterventionModuleState/getDemandesInterventionsAttachment'];
        },
        isAttachmentLoading() {
            return store.getters['suiviDemandeInterventionModuleState/getDemandesInterventionsIsAttachmentLoading'];
        },
        contentType() {
            return store.getters['suiviDemandeInterventionModuleState/getDemandesInterventionsAttachmentType'];
        }
    },
    methods: {
        goToPage(item: { id: { pageName: string; value: number; }; }){
            router.push({ name: item.id.pageName, params: { id: item.id.value } });
        },
    }
});
</script>

<style scoped>
.regles-pastilles-group {
    display: inline-flex;
    align-items: center;
    gap: 16px;
}
.container {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 24px;
}
.title-container {
    color: var(--color-principal);
    font-size: var(--font-size-large);
    font-weight: var(--font-weight-600);
    margin-bottom: 15px;
}
</style>
